import { Injectable } from '@angular/core';
import { Observable, switchMap, map, catchError, throwError } from 'rxjs';
import { ReceivingLogApiService } from './api/receiving-log-api.service';
import { AssetsDataService } from '../../service/assets-data.service';
import { FormattedPoValues } from '../../models/poData/FormattedPoValues';

export type PoLookupFailureReason = 'invalidPo' | 'validationFailed' | 'fetchFailed';

/** The two sequential backend calls a lookup makes, for loading-spinner text. */
export type PoLookupPhase = 'validating' | 'fetching';

/**
 * Typed error emitted by PoLookupService.lookup. `userMessage` is ready to show
 * to the user (sourced from the configured toast text).
 */
export class PoLookupError extends Error {
  constructor(
    public reason: PoLookupFailureReason,
    public userMessage: string,
    public override cause?: unknown
  ) {
    super(userMessage);
    this.name = 'PoLookupError';
  }
}

/**
 * Stateless PO lookup: validates a PO number against the backend and, if valid,
 * fetches and formats its data. Returns the result as an Observable — no
 * BehaviorSubject side effects, so both the desktop form and the mobile flow
 * can consume it directly.
 */
@Injectable({
  providedIn: 'root',
})
export class PoLookupService {
  constructor(
    private _api: ReceivingLogApiService,
    private _assetsDataService: AssetsDataService
  ) {}

  /**
   * Validates the PO number and fetches its formatted data.
   *
   * @param poNumber The PO number to validate and fetch data for.
   * @param onPhase Optional callback fired as the lookup moves between its two
   *   backend calls ('validating' immediately, 'fetching' once validation
   *   passes) — lets callers update their loading-spinner text per phase.
   * @returns The formatted PO values; errors with a PoLookupError on failure
   *   ('invalidPo' when the PO doesn't exist, 'validationFailed'/'fetchFailed'
   *   for request errors).
   */
  public lookup(
    poNumber: string,
    onPhase?: (phase: PoLookupPhase) => void,
  ): Observable<FormattedPoValues> {
    const toastText = this._assetsDataService.GetUiConfigData().toastText;

    onPhase?.('validating');
    return this._api.getPoValidation(poNumber).pipe(
      catchError((err) =>
        throwError(
          () => new PoLookupError('validationFailed', toastText.po.validation.error, err)
        )
      ),
      switchMap((poValid: boolean) => {
        if (!poValid) {
          return throwError(
            () => new PoLookupError('invalidPo', toastText.po.validation.nonExistent)
          );
        }
        onPhase?.('fetching');
        return this._api.getPoData(poNumber).pipe(
          map((poData) => new FormattedPoValues(poData)),
          catchError((err) =>
            throwError(
              () => new PoLookupError('fetchFailed', toastText.po.fetch.error, err)
            )
          )
        );
      })
    );
  }
}
